import type { FileType } from '@/components/evidence-viewer'

export type ChatAttachmentKind =
  | 'image'
  | 'pdf'
  | 'docx'
  | 'xlsx'
  | 'csv'
  | 'txt'
  | 'md'
  | 'json'
  | 'unsupported'

export type ChatAttachmentPresentation = {
  kind: ChatAttachmentKind
  label: string
  readableByAi: boolean
  previewType: FileType
}

const AI_READABLE_KINDS = new Set<ChatAttachmentKind>([
  'image',
  'pdf',
  'docx',
  'xlsx',
  'csv',
  'txt',
  'md',
  'json',
])

function getExtension(filename: string): string {
  const match = filename.toLowerCase().match(/\.([a-z0-9]+)$/)
  return match?.[1] ?? ''
}

export function getChatAttachmentPresentation(
  contentType?: string | null,
  filename = ''
): ChatAttachmentPresentation {
  const mime = (contentType ?? '').toLowerCase()
  const ext = getExtension(filename)

  let kind: ChatAttachmentKind = 'unsupported'

  if (
    mime.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)
  ) {
    kind = 'image'
  } else if (mime === 'application/pdf' || ext === 'pdf') {
    kind = 'pdf'
  } else if (
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ext === 'docx'
  ) {
    kind = 'docx'
  } else if (
    mime ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    ext === 'xlsx'
  ) {
    kind = 'xlsx'
  } else if (mime === 'text/csv' || ext === 'csv') {
    kind = 'csv'
  } else if (mime === 'text/plain' || ext === 'txt') {
    kind = 'txt'
  } else if (
    mime === 'text/markdown' ||
    ['md', 'mdx', 'markdown'].includes(ext)
  ) {
    kind = 'md'
  } else if (mime === 'application/json' || ext === 'json') {
    kind = 'json'
  }

  return {
    kind,
    label: getAttachmentLabel(kind),
    readableByAi: AI_READABLE_KINDS.has(kind),
    previewType: getPreviewType(kind),
  }
}

function getAttachmentLabel(kind: ChatAttachmentKind): string {
  switch (kind) {
    case 'pdf':
      return 'PDF'
    case 'docx':
      return 'DOCX'
    case 'xlsx':
      return 'XLSX'
    case 'csv':
      return 'CSV'
    case 'txt':
      return 'TXT'
    case 'md':
      return 'MD'
    case 'json':
      return 'JSON'
    case 'image':
      return 'Image'
    default:
      return 'Not readable'
  }
}

function getPreviewType(kind: ChatAttachmentKind): FileType {
  switch (kind) {
    case 'image':
      return 'image'
    case 'pdf':
      return 'pdf'
    case 'docx':
      return 'docx'
    case 'xlsx':
      return 'xlsx'
    case 'md':
      return 'markdown'
    case 'csv':
    case 'txt':
    case 'json':
      return 'text'
    default:
      return 'other'
  }
}
